import React from 'react';
import {
  IFarmInputProduct,
  IFarmInputProgramLineInput,
} from 'constants/interfaces';
import {Box, Card, IconButton, Tooltip, Typography} from '@material-ui/core';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TableFooter from '@material-ui/core/TableFooter';
import EditIcon from '@material-ui/icons/Edit';
import CloseIcon from '@material-ui/icons/Close';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSeedling} from '@fortawesome/pro-solid-svg-icons';

import './ProgramLineTable.scss';

/* -----------------------------------------------------------
   Helpers
----------------------------------------------------------- */
const toNumber = (v: any): number | undefined =>
  v === undefined || v === null ? undefined : Number(v);

const formatRand = (val: number) =>
  `R ${val.toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/* -----------------------------------------------------------
   Main Table
----------------------------------------------------------- */
export function ProgramLineTable(props: {
  lines: IFarmInputProgramLineInput[];
  allProducts: IFarmInputProduct[];
  onEdit: (index: number) => void;
  onCopy: (index: number) => void;
  onRemove: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}) {
  /* ---------- log every raw/parsed/catalogue price ---------- */
  props.lines.forEach((line) => {
    const _prod = props.allProducts.find(
      (p) => String(p.id) === String(line.productId),
    );
    console.log('[PriceDebug]', {
      id: line.productId,
      override: line.price,
      catalogue: _prod?.price,
    });
  });

  /* ----- grand total across all lines ----- */
  const programTotal = props.lines.reduce((acc, line) => {
    const prod = props.allProducts.find(
      (p) => String(p.id) === String(line.productId),
    );
    const price = toNumber(line.price) ?? prod?.price;
    return price ? acc + price * (line.quantity ?? 0) : acc;
  }, 0);

  /* -------- empty state -------- */
  if (!props.lines || props.lines.length === 0) {
    return (
      <Box className="empty-lines-container">
        <FontAwesomeIcon icon={faSeedling} className="seedling-icon" />
        <Typography variant="h6" className="empty-heading">
          Build Your Program
        </Typography>
        <Typography variant="body2" className="empty-subheading">
          Get started by adding products to create a tailored program for your
          farmers.
        </Typography>
      </Box>
    );
  }

  return (
    <Card variant="outlined" className="program-line-table-container">
      <TableContainer className="program-line-table">
        <Table aria-label="program line table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Stage</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Application&nbsp;Rate</TableCell>
              <TableCell
                align="right"
                style={{whiteSpace: 'nowrap', width: 120}}
              >
                Price
              </TableCell>
              <TableCell>Qty</TableCell>
              <TableCell
                align="right"
                style={{whiteSpace: 'nowrap', width: 140}}
              >
                Total
              </TableCell>
              <TableCell style={{width: '32%'}}>Comments</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {props.lines.map((line, index) => (
              <ProgramLineItemRow
                key={index}
                lineItem={line}
                allProducts={props.allProducts}
                onEdit={() => props.onEdit(index)}
                onCopy={() => props.onCopy(index)}
                onMoveUp={() => props.onMoveUp(index)}
                onMoveDown={() => props.onMoveDown(index)}
                onRemove={() => props.onRemove(index)}
              />
            ))}
          </TableBody>

          {/* -------- grand-total footer row -------- */}
          <TableFooter>
            <TableRow>
              <TableCell colSpan={4} />
              <TableCell
                align="right"
                style={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  whiteSpace: 'nowrap',
                }}
              >
                Program&nbsp;Total
              </TableCell>
              <TableCell
                align="right"
                style={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatRand(programTotal)}
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Card>
  );
}

/* -----------------------------------------------------------
   Row Component
----------------------------------------------------------- */
function ProgramLineItemRow(props: {
  lineItem: IFarmInputProgramLineInput;
  allProducts: IFarmInputProduct[];
  onEdit: VoidFunction;
  onCopy: VoidFunction;
  onRemove: VoidFunction;
  onMoveUp: VoidFunction;
  onMoveDown: VoidFunction;
}) {
  const {lineItem} = props;

  const product = props.allProducts.find(
    (p) => String(p.id) === String(lineItem.productId),
  );

  const unitPrice = toNumber(lineItem.price) ?? product?.price;

  const total =
    unitPrice != null && lineItem.quantity != null
      ? unitPrice * lineItem.quantity
      : undefined;

  return (
    <TableRow className="program-line-table-row">
      <TableCell>{lineItem.when}</TableCell>
      <TableCell>{product?.name || '—'}</TableCell>
      <TableCell>
        {lineItem.applicationRate}
        {lineItem.applicationUnit} {lineItem.applicationType}
      </TableCell>
      <TableCell align="right" style={{whiteSpace: 'nowrap'}}>
        {unitPrice != null ? formatRand(unitPrice) : '—'}
      </TableCell>
      <TableCell>{lineItem.quantity}</TableCell>
      <TableCell align="right" style={{whiteSpace: 'nowrap'}}>
        {total != null ? formatRand(total) : '—'}
      </TableCell>
      <TableCell style={{width: '32%'}}>{lineItem.comment}</TableCell>
      <TableCell align="right">
        <Box className="actions">
          <Tooltip title="Edit">
            <IconButton size="small" onClick={props.onEdit}>
              <EditIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Copy">
            <IconButton size="small" onClick={props.onCopy}>
              <FileCopyOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Move up">
            <IconButton size="small" onClick={props.onMoveUp}>
              <ArrowUpwardIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Move down">
            <IconButton size="small" onClick={props.onMoveDown}>
              <ArrowDownwardIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Remove">
            <IconButton size="small" onClick={props.onRemove}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
}
